import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { CreateCustomerGroupPayloadProps, createCustomerGroupResProps, CreateCustomerGroupResProps, CustomerDataProps, CustomerGroup, GetAllPosCustomerGroupPayloadProps, GetAllPosCustomerGroupProps, GetAllPosCustomerGroupResProps, GetAllPosCustomersPayloadProps, GetAllPosCustomersResProps, GetAllPosMappingCustomersPayloadProps, GetAllPosMappingCustomersResProps, GetOnePosCustomerGroupPayloadProps, GetOnePosCustomerGroupResProps, OnRemoveCustomerGroupPayloadProps, OnRemoveCustomerGroupResProps, UpdateCustomerGroupPayloadProps, updateCustomerGroupRes } from "../../components/customerGroups/customerGroupModule";
import HttpRoutingService from "../../shared/services/httpRoutingService";
import { API } from "../../shared/apiRoutes";

//Type specification for initialState
interface IntialValueProps {
    loading: boolean,
    error: string | undefined,
    customerGroupDetails: {
        groupName: string,
        description: string,
        customerGroupMappings: [],
        id: number | null
    },
    customers: {
        rows: CustomerDataProps[]
    },
    getAllPosCustomerGroup: {
        loading: boolean,
        value: GetAllPosCustomerGroupResProps | null,
        error: string,
        isReached: boolean,
        filter: string | null
    },
    // Cached paginated customer list (used in AddEditCustomerGroups)
    allPosCustomers: {
        loading: boolean,
        value: GetAllPosCustomersResProps | null,
        error: string,
        isReached: boolean,
        filter: string | null   // JSON fingerprint of {searchText, filterData}
    },
    // Cached mapping customers list (view mode)
    mappingCustomers: {
        loading: boolean,
        value: GetAllPosMappingCustomersResProps | null,
        error: string
    }
}
//Defining value for initialState
const initialState: IntialValueProps = {
    loading: false,
    error: '',
    customerGroupDetails: {
        groupName: '',
        description: '',
        customerGroupMappings: [],
        id: null
    },
    customers: {
        rows: []
    },
    getAllPosCustomerGroup: {
        loading: false,
        value: null,
        error: '',
        isReached: false,
        filter: null
    },
    allPosCustomers: {
        loading: false,
        value: null,
        error: '',
        isReached: false,
        filter: null
    },
    mappingCustomers: {
        loading: false,
        value: null,
        error: ''
    }
}


//Getting all pos customer group using getAllPosCustomerGroupRedux api call using createAsyncThunk
export const getAllPosCustomerGroupRedux = createAsyncThunk('customerGroup/getAllPosCustomerGroup', ({ storeId, data }: { storeId: number, data: any }) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosCustomerGroup, { storeId: storeId })
    return HttpRoutingService?.getMethod<GetAllPosCustomerGroupResProps>(url, data)
})

//On removing customer group using onRemoveCustomerGroupRedux api call using createAsyncThunk
export const onRemoveCustomerGroupRedux = createAsyncThunk('customerGroup/onRemoveCustomerGroup', ({ storeId, id }: OnRemoveCustomerGroupPayloadProps) => {
    const url = HttpRoutingService.replaceUrl(API.getOnePosCustomerGroup, { storeId: storeId, id: id });
    return HttpRoutingService.deleteMethod<OnRemoveCustomerGroupResProps>(url);
})

//Create customer group using createCustomerGroupRedux api call using createAsyncThunk
export const createCustomerGroupRedux = createAsyncThunk('customerGroup/createCustomerGroup', ({ storeId, data }: CreateCustomerGroupPayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosCustomerGroup, { storeId: storeId })
    return HttpRoutingService?.postMethod<createCustomerGroupResProps>(url, data)
})

//Getting one pos customer group using getOnePosCustomerGroupRedux api call using createAsyncThunk
export const getOnePosCustomerGroupRedux = createAsyncThunk('customerGroup/getOnePosCustomerGroup', ({ storeId, id }: GetOnePosCustomerGroupPayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosCustomerGroup, { storeId: storeId, id: id })
    return HttpRoutingService?.getMethod<GetOnePosCustomerGroupResProps>(url)
})

//Getting all pos mapping customer group using getAllPosMappingCustomersRedux api call using createAsyncThunk
export const getAllPosMappingCustomersRedux = createAsyncThunk('customerGroup/getAllPosMappingCustomers', ({ storeId, customerGroupId, data }: GetAllPosMappingCustomersPayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPosMappingCustomers, { storeId: storeId, customerGroupId: customerGroupId })
    return HttpRoutingService?.getMethod<GetAllPosMappingCustomersResProps>(url, data)
})

//Getting all pos mapping customer group using getAllPosMappingCustomersRedux api call using createAsyncThunk
export const updateCustomerGroupRedux = createAsyncThunk('customerGroup/updateCustomerGroup', ({ storeId, customerGroupId, data }: UpdateCustomerGroupPayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getOnePosCustomerGroup, { storeId: storeId, id: customerGroupId })
    return HttpRoutingService?.putMethod<updateCustomerGroupRes>(url, data)
})


//Getting all pos customer using getAllPosCustomersRedux api call using createAsyncThunk
export const getAllPosCustomersRedux = createAsyncThunk('customerGroup/getAllPosCustomers', ({ storeId, data }: GetAllPosCustomersPayloadProps) => {
    const url = HttpRoutingService?.replaceUrl(API?.getAllPOSCustomer, { storeId: storeId })
    return HttpRoutingService?.getMethod<GetAllPosCustomersResProps>(url, data)
})

//Customer Group Slice 
const customerGroupSlice = createSlice({
    name: 'customerGroup',
    initialState,
    reducers: {
        // Wipe the allPosCustomers cache so the next fetch always hits the API
        resetCustomersCache: (state) => {
            state.allPosCustomers = {
                loading: false,
                value: null,
                error: '',
                isReached: false,
                filter: null
            }
            state.mappingCustomers = {
                loading: false,
                value: null,
                error: ''
            }
        }
    },
    extraReducers(builder) {
        // getOnePosCustomerGroupRedux
        builder.addCase(getOnePosCustomerGroupRedux.pending, (state) => {
            state.loading = true
        })
        builder.addCase(getOnePosCustomerGroupRedux.fulfilled, (state, action) => {
            state.loading = false
            state.customerGroupDetails = action.payload?.customerGroupDetails
        })
        builder.addCase(getOnePosCustomerGroupRedux.rejected, (state, action) => {
            state.loading = false
            state.error = action.error.message
        })
        // getAllPosCustomersRedux — paginated list with caching
        builder.addCase(getAllPosCustomersRedux.pending, (state) => {
            state.allPosCustomers.loading = true
        })
        builder.addCase(getAllPosCustomersRedux.fulfilled, (state, action) => {
            state.allPosCustomers.loading = false
            const offset = action.meta.arg.data?.offset ?? 0
            const limit = action.meta.arg.data?.limit ?? 10
            const incoming = action.payload?.customers?.rows ?? []
            const prev = state.allPosCustomers.value
            if (offset > 0 && prev) {
                // Append paginated rows
                state.allPosCustomers.value = {
                    customers: {
                        rows: [...prev.customers.rows, ...incoming]
                    }
                }
            } else {
                state.allPosCustomers.value = action.payload
            }
            state.allPosCustomers.isReached = incoming.length < limit
            state.allPosCustomers.filter = JSON.stringify({
                searchText: action.meta.arg.data?.searchText,
                filterData: action.meta.arg.data?.filterData
            })
            // Also keep legacy customers field in sync (backward compat)
            state.customers = action.payload?.customers
        })
        builder.addCase(getAllPosCustomersRedux.rejected, (state, action) => {
            state.allPosCustomers.loading = false
            state.allPosCustomers.error = action.error.message as string
        })
        // getAllPosMappingCustomersRedux — simple replace
        builder.addCase(getAllPosMappingCustomersRedux.pending, (state) => {
            state.mappingCustomers.loading = true
        })
        builder.addCase(getAllPosMappingCustomersRedux.fulfilled, (state, action) => {
            state.mappingCustomers.loading = false
            state.mappingCustomers.value = action.payload
        })
        builder.addCase(getAllPosMappingCustomersRedux.rejected, (state, action) => {
            state.mappingCustomers.loading = false
            state.mappingCustomers.error = action.error.message as string
        })
        // getAllPosCustomerGroupRedux — paginated list with caching
        builder.addCase(getAllPosCustomerGroupRedux.pending, (state) => {
            state.getAllPosCustomerGroup.loading = true
        })
        builder.addCase(getAllPosCustomerGroupRedux.fulfilled, (state, action) => {
            state.getAllPosCustomerGroup.loading = false
            const offset = action.meta.arg.data?.offset ?? 0
            const limit = action.meta.arg.data?.limit ?? 10
            const incoming = action.payload?.customergroup?.rows ?? []
            const prev = state.getAllPosCustomerGroup.value
            if (offset > 0 && prev) {
                // Append pages
                state.getAllPosCustomerGroup.value = {
                    customergroup: {
                        count: action.payload?.customergroup?.count ?? prev.customergroup.count,
                        rows: [...prev.customergroup.rows, ...incoming]
                    }
                }
            } else {
                state.getAllPosCustomerGroup.value = action.payload
            }
            state.getAllPosCustomerGroup.isReached = incoming.length < limit
            state.getAllPosCustomerGroup.filter = JSON.stringify({
                globalsearch: action.meta.arg.data?.globalsearch,
                filterData: action.meta.arg.data?.filterData
            })
        })
        builder.addCase(getAllPosCustomerGroupRedux.rejected, (state, action) => {
            state.getAllPosCustomerGroup.loading = false
            state.getAllPosCustomerGroup.error = action.error.message as string
        })
    },
})

export const { resetCustomersCache } = customerGroupSlice.actions

export default customerGroupSlice.reducer